import { visibleTourTarget } from "@/lib/tour/target";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import "./tour-theme.css";
import { useAuth } from "@/contexts/AuthContext";
import { useUiPref } from "@/hooks/useUiPref";
import { getTourSteps, tourRoleForMainRole, type TourStep } from "@/lib/tour/steps";
import { TourWelcome, TourFinish } from "./TourOverlays";

/**
 * Custom event any page can dispatch to (re)launch the product tour. The
 * Settings "Take a tour" button uses a sessionStorage flag instead (it survives
 * the "/" → "/hr-dashboard" redirect), but this event is handy when already on
 * a dashboard.
 */
export const START_TOUR_EVENT = "besthr:start-tour";

type DriverInstance = ReturnType<typeof driver>;

/** Poll for a selector to appear (and have a non-zero box) after navigation. */
function waitForSelector(sel: string | undefined, timeout = 4000): Promise<void> {
  return new Promise((resolve) => {
    if (!sel) return resolve();
    const start = Date.now();
    const tick = () => {
      const el = document.querySelector(sel) as HTMLElement | null;
      if (el && el.getBoundingClientRect().height > 0) return resolve();
      if (Date.now() - start > timeout) return resolve();
      requestAnimationFrame(tick);
    };
    tick();
  });
}

/**
 * Route-persistent tour controller (mounted once in App so it survives
 * navigation between modules). Shows an animated welcome screen, runs the
 * role-specific driver.js spotlight sequence, then a finish screen. Auto-starts
 * once per user (until completed/skipped, tracked in user_ui_prefs) and supports
 * manual replay.
 */
export function ProductTour() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const role = tourRoleForMainRole(user?.role?.mainRole);
  const [done, setDone] = useUiPref(`tour_done_${role}`, false, { enabled: !!user });
  const autoStarted = useRef(false);
  const running = useRef(false);

  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [finishOpen, setFinishOpen] = useState(false);

  const { canAccess, ready } = useModuleAccess();
  const isModuleEnabled = (key?: string) => !key || canAccess(key);

  const markDone = () => {
    if (!done) setDone(true);
  };

  // Show the animated welcome screen; the driver sequence starts on "Start".
  const beginTour = () => {
    if (running.current) return;
    running.current = true;
    try { sessionStorage.removeItem("besthr_start_tour"); } catch {}
    setFinishOpen(false);
    setWelcomeOpen(true);
  };

  const skipFromWelcome = () => {
    setWelcomeOpen(false);
    running.current = false;
    markDone();
  };

  // Run the driver.js spotlight sequence (welcome step is the React overlay).
  const runDriver = () => {
    setWelcomeOpen(false);

    const steps = getTourSteps(role, t).filter(step => isModuleEnabled(step.moduleKey) && (step.route || visibleTourTarget(step.element)));
    if (steps.length === 0) {
      running.current = false;
      markDone();
      return;
    }

    const driverSteps: DriveStep[] = steps.map((st) => ({
      element: st.element,
      popover: { title: st.title, description: st.description, side: st.side, align: st.align },
    }));

    let completed = false;
    const dRef = { current: null as DriverInstance | null };

    const resolveIndex = async (idx: number, dir: 1 | -1): Promise<number> => {
      while (idx >= 0 && idx < steps.length) {
        const st: TourStep = steps[idx];
        if (!isModuleEnabled(st.moduleKey)) {
          steps.splice(idx, 1);
          driverSteps.splice(idx, 1);
          dRef.current?.setSteps(driverSteps);
          if (dir === -1) idx -= 1;
          continue;
        }
        window.dispatchEvent(new Event("besthr:tour-open-sections"));
        if (st.route && window.location.pathname !== st.route) {
          navigate(st.route);
        }
        await waitForSelector(st.element);
        if (!st.element || visibleTourTarget(st.element)) return idx;
        steps.splice(idx, 1);
        driverSteps.splice(idx, 1);
        dRef.current?.setSteps(driverSteps);
        if (dir === -1) idx -= 1;
      }
      return -1;
    };

    const goTo = async (target: number, dir: 1 | -1) => {
      const resolved = await resolveIndex(target, dir);
      if (resolved === -1) {
        if (dir === 1) { completed = true; dRef.current?.destroy(); }
        return;
      }
      dRef.current?.moveTo(resolved);
    };

    const d = driver({
      showProgress: true,
      allowClose: true,
      smoothScroll: true,
      popoverClass: "besthr-tour",
      nextBtnText: t("tour.common.next", "Next"),
      prevBtnText: t("tour.common.prev", "Back"),
      doneBtnText: t("tour.common.done", "Done"),
      progressText: t("tour.common.progress", "{{current}} of {{total}}"),
      steps: driverSteps,
      onNextClick: () => { void goTo((d.getActiveIndex() ?? 0) + 1, 1); },
      onPrevClick: () => { void goTo((d.getActiveIndex() ?? 0) - 1, -1); },
      onHighlightStarted: (element) => {
        if (element instanceof HTMLElement) {
          element.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
          setTimeout(() => d.refresh(), 350);
        }
      },
      onDestroyed: () => {
        markDone();
        if (completed) {
          // Keep `running` true until the finish screen is dismissed.
          setFinishOpen(true);
        } else {
          running.current = false;
        }
      },
    });
    dRef.current = d;

    window.dispatchEvent(new Event("besthr:tour-open-sections"));
    requestAnimationFrame(async () => {
      const first = await resolveIndex(0, 1);
      if (first === -1) { d.destroy(); return; }
      d.drive(first);
    });
  };

  const closeFinish = () => {
    setFinishOpen(false);
    running.current = false;
  };

  // Auto-start on whichever dashboard the user lands on. HR/admin are redirected
  // from "/" to "/hr-dashboard", so both routes are eligible. The "/" redirect
  // also drops the URL query, so a forced replay uses a sessionStorage flag that
  // survives the redirect, with the ?startTour=1 param as a fallback.
  useEffect(() => {
    if (!user || !ready) return; // not authenticated
    const onDashboard = location.pathname === "/" || location.pathname === "/hr-dashboard";
    if (!onDashboard) return;
    if (done === undefined) return; // pref still loading

    const params = new URLSearchParams(location.search);
    const flagged = sessionStorage.getItem("besthr_start_tour") === "1";
    const forced = flagged || params.get("startTour") === "1";

    if (forced) {
      try { sessionStorage.setItem("besthr_start_tour", "1"); } catch {}
      if (params.get("startTour") === "1") {
        const url = new URL(window.location.href);
        url.searchParams.delete("startTour");
        window.history.replaceState({}, "", url.toString());
      }
      autoStarted.current = true; // don't let the auto path fire afterwards
      const id = window.setTimeout(() => beginTour(), 400);
      return () => window.clearTimeout(id);
    }

    if (autoStarted.current || done) return;
    autoStarted.current = true;
    const id = window.setTimeout(() => beginTour(), 600);
    return () => window.clearTimeout(id);
  }, [location.pathname, location.search, done, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // Manual replay via custom event.
  useEffect(() => {
    const handler = () => beginTour();
    window.addEventListener(START_TOUR_EVENT, handler);
    return () => window.removeEventListener(START_TOUR_EVENT, handler);
  }, [role, done]);

  return (
    <>
      <TourWelcome
        open={welcomeOpen}
        title={t(`tour.${role}.welcome.title`)}
        description={t(`tour.${role}.welcome.description`)}
        startLabel={t("tour.common.start", "Start tour")}
        skipLabel={t("tour.common.skip", "Skip")}
        onStart={runDriver}
        onSkip={skipFromWelcome}
      />
      <TourFinish
        open={finishOpen}
        title={t(`tour.${role}.finish.title`)}
        description={t(`tour.${role}.finish.description`)}
        doneLabel={t("tour.common.done", "Done")}
        onDone={closeFinish}
      />
    </>
  );
}
